import type { ReactNode } from 'react';
import React, { useEffect, forwardRef, useRef, useCallback } from 'react';

type InfiniteScrollProps = React.PropsWithChildren<{
	onScrollTo: () => void;
	// If the cursor is not passed in, onScrollTo will only be called once.
	cursor?: string | null | undefined;
	observerOptions: IntersectionObserverInit;
}>;

export const InfiniteScroll = ({
	onScrollTo,
	cursor,
	observerOptions,
	children,
}: InfiniteScrollProps) => {
	const loaderTarget = useRef<HTMLDivElement>(null);
	const previousCursor = useRef<string | null | undefined>(null);
	// This function is called by the IntersectionObserver API when "loaderTarget" UI element
	// changes visibility.  If it has become visible (typically by user scrolling), call our
	// scroll handler and the parent will attempt to load more data
	const handleIntersect = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			// Sometimes based on an unknown render race condition, the previous IntersectionObserver is not
			// properly cleared before the next one is observed, which causes the 0th indexed IntersectionObserver
			// to never be intersecting, which causes the onScrollTo callback to not be called when it should be.
			// To fix this just get rid of the old observer if it is not properly cleaned up.
			if (entries.length > 1) {
				entries.shift();
			}
			// On the first scroll the previousCursor = null and cursor is a string or undefined
			// Intersection can occur multiple times but we only call onScrollTo again if we have a new cursor.
			if (entries?.[0]?.isIntersecting && cursor !== previousCursor.current) {
				previousCursor.current = cursor;
				onScrollTo();
			}
		},
		[onScrollTo, cursor],
	);

	// IntersectionObserver detects when our target element becomes visible
	useEffect(() => {
		// Adding a positive 'rootMargin' and setting 'root' to the scroll container *should* trigger
		// a positive 'intersection' result (thus loading results) before this element even comes into view
		// (when it is Xpx away from the viewport). 'rootMargin' follows CSS 'margin' syntax.
		// However, this doesn't seem to be working, intersection only triggers when element is in viewport.
		// Have also attempted to pass in a ref from the table tree container instead of querying the document,
		// but that also does not work.
		// @TODO figure out what is wrong here.
		// eslint-disable-next-line
		const observer: IntersectionObserver = new IntersectionObserver(
			handleIntersect,
			observerOptions,
		);

		const target = loaderTarget?.current;
		if (target) {
			observer.observe(target);
		}
		return () => {
			if (target) {
				observer.unobserve(target);
			}
			observer.disconnect();
		};
	}, [loaderTarget, handleIntersect, observerOptions]);

	return <LoaderTarget ref={loaderTarget}>{children}</LoaderTarget>;
};

type LoaderTargetProps = {
	children: ReactNode;
};

const LoaderTarget = forwardRef<HTMLDivElement, LoaderTargetProps>(
	({ children }, ref: React.Ref<HTMLDivElement>) => <div ref={ref}>{children}</div>,
);
